import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardLayout } from '../../components/templates/DashboardLayout.js';
import { SurveyCard } from '../../components/molecules/SurveyCard.js';
import { Spinner } from '../../components/atoms/Spinner.js';
import { EmptyState } from '../../components/molecules/EmptyState.js';
import { ErrorBanner } from '../../components/molecules/ErrorBanner.js';
import { Typography } from '../../components/atoms/Typography.js';
import { Button } from '../../components/atoms/Button.js';
import { StatsOverview } from '../../components/organisms/StatsOverview.js';
import { useDashboardStore } from '../../stores/dashboard-store.js';
import { get } from '../../lib/api-client.js';
interface SurveySummaryResponse {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  currentRespondents: number;
  maxRespondents: number;
}

/** Backend paginated response shape */
interface SurveyListApiResponse {
  success: boolean;
  data: {
    items: SurveySummaryResponse[];
    total: number;
    offset: number;
    limit: number;
  };
}

const LIMIT = 20;

type StatusFilter = 'all' | 'draft' | 'active' | 'paused' | 'completed' | 'archived';

export function SurveyListPage() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { surveys, total, loading, error, setSurveys, setLoading, setError } = useDashboardStore();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [offset, setOffset] = useState(0);

  const fetchSurveys = useCallback(
    async (currentOffset: number, append: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          offset: String(currentOffset),
          limit: String(LIMIT),
        });
        if (statusFilter !== 'all') {
          params.set('status', statusFilter);
        }

        const res = await get<SurveyListApiResponse>(`/api/v1/surveys?${params.toString()}`);

        if (append) {
          setSurveys([...surveys, ...res.data.items], res.data.total);
        } else {
          setSurveys(res.data.items, res.data.total);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('fetch_error'));
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, surveys, setSurveys, setLoading, setError, t],
  );

  useEffect(() => {
    setOffset(0);
    fetchSurveys(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const handleLoadMore = () => {
    const nextOffset = offset + LIMIT;
    setOffset(nextOffset);
    fetchSurveys(nextOffset, true);
  };

  const handleSurveyClick = (id: string) => {
    navigate(`/dashboard/${id}`);
  };

  const activeSurveys = surveys.filter(
    (s) => s.status === 'active' || s.status === 'interviewing',
  ).length;
  const completedSurveys = surveys.filter(
    (s) => s.status === 'completed' || s.status === 'report_generated',
  ).length;
  const completionRate = total > 0 ? Math.round((completedSurveys / total) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <Typography variant="h2">{t('title')}</Typography>

        <StatsOverview
          totalSurveys={total}
          activeSurveys={activeSurveys}
          totalRespondents={0}
          completionRate={completionRate}
        />

        <div className="flex items-center justify-between">
          <Typography variant="h3">{t('survey_list')}</Typography>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="all">{t('all_statuses')}</option>
            <option value="draft">{t('survey_status.draft')}</option>
            <option value="active">{t('survey_status.active')}</option>
            <option value="paused">{t('survey_status.paused')}</option>
            <option value="completed">{t('survey_status.completed')}</option>
            <option value="archived">{t('survey_status.archived')}</option>
          </select>
        </div>

        {error && <ErrorBanner message={error} onRetry={() => fetchSurveys(0, false)} />}

        {loading && surveys.length === 0 ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : surveys.length === 0 ? (
          <EmptyState title={t('empty_state.title')} description={t('empty_state.description')} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {surveys.map((survey) => (
                <SurveyCard
                  key={survey.id}
                  id={survey.id}
                  title={survey.title}
                  status={survey.status}
                  createdAt={survey.createdAt}
                  respondentCount={survey.currentRespondents}
                  maxRespondents={survey.maxRespondents}
                  onClick={handleSurveyClick}
                />
              ))}
            </div>

            {surveys.length < total && (
              <div className="flex justify-center pt-4">
                <Button variant="secondary" onClick={handleLoadMore} loading={loading}>
                  {t('button.load_more', { ns: 'common' })}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
